import {
  DoubleSide,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  Texture,
  TextureLoader,
  Vector3
} from "three";
import { CameraHandler, CameraImage } from ".";
import { MarkerNavType } from "../../../redux/settings-slice";
import { CustomMouseEvent } from "../controls";
import arrowPathStandard from '../../textures/misc/arrow-standard.png';
import arrowPathShadow from '../../textures/misc/arrow-shadow.png';
import { RayCaster } from "../ray-caster";

const loader = new TextureLoader();

export class SceneViewCameras {
  private cameraHandler: CameraHandler;
  private arrowNavigation: ArrowNavigation;
  public markerNavigation: MarkerNavigation;
  private modifier = false;
  private navigationType: MarkerNavType;

  constructor(cameraHandler: CameraHandler) {
    this.cameraHandler = cameraHandler;
    this.markerNavigation = new MarkerNavigation(this);
    this.arrowNavigation = new ArrowNavigation(this);

    this.initEvents();
  }

  get viewer() {
    return this.cameraHandler.viewer;
  }

  get files() {
    return this.cameraHandler.files;
  }

  get scene() {
    return this.cameraHandler.markerScene;
  }

  get renderInFront() {
    return this.viewer.orbitState || this.arrowNavigation.visible;
  }

  get markersVisible() {
    return this.markerNavigation.visible;
  }

  initEvents() {
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    window.addEventListener( 'keydown', this.onKeyDown, false);
    window.addEventListener( 'keyup', this.onKeyUp, false);
  }

  removeEvents() {
    window.removeEventListener('keydown', this.onKeyDown, false);
    window.removeEventListener('keyup', this.onKeyUp, false);
  }

  onKeyUp(event) {
    if (event.which === 17) {
      this.modifier = false;
    }
  }

  onKeyDown(event) {
    if (event.which === 17) {
      this.modifier = true;
    }
  }

  onMouseUp(event: CustomMouseEvent): boolean {
    if (event.mouseMoved) return false;
    if (!event.isLeftClick) return false;

    const camera = this.markerNavigation.onMouseUp(event)
      || this.arrowNavigation.onMouseUp(event);

    if (camera) {
      this.viewer.loadImage(camera.id);
      return true;
    }

    return false;
  }

  onMouseMove(event: CustomMouseEvent) {
    if (event.mouseMoved) return;
    this.arrowNavigation.onMouseMove(event);
    this.markerNavigation.onMouseMove(event);
  }

  setNavigationType(type: MarkerNavType) {
    this.navigationType = type;
    this.markerNavigation.updatePicker();
  }

  updateArrowNavigation() {
    this.arrowNavigation.updateNavigation();
  }

  clearArrowNavigation() {
    this.arrowNavigation.clearNavigation();
  }

  updateVisibility() {
    const isOrbitMode = this.viewer.orbitState;
    const isHidden = this.navigationType === MarkerNavType.None;
    const isMarkerMode = this.navigationType === MarkerNavType.Markers;
    const isArrowMode = this.navigationType === MarkerNavType.Arrows;

    if (isHidden) {
      this.markerNavigation.setVisible(false);
      this.arrowNavigation.setVisible(false);
    } else {
      if (isMarkerMode) {
        this.markerNavigation.setVisible(true);
        this.arrowNavigation.setVisible(false);
      } else if (isArrowMode) {
        if (isOrbitMode) {
          this.markerNavigation.setVisible(true);
          this.arrowNavigation.setVisible(false);
        } else {
          this.markerNavigation.setVisible(this.modifier);
          this.arrowNavigation.setVisible(!this.modifier);
        }
      }
    }

    this.markerNavigation.setEnabled(isArrowMode || isMarkerMode);
    this.arrowNavigation.setEnabled(isArrowMode);
  }

  update() {
    this.updateVisibility();
    this.files.forEach(file => file.update());
    this.arrowNavigation.update();
  }
}

class MarkerNavigation {
  private sceneViewController: SceneViewCameras;
  private selected: CameraImage;
  public enabled = false;
  public visible = false;

  constructor(sceneViewCameras: SceneViewCameras) {
    this.sceneViewController = sceneViewCameras;
  }

  get files() {
    return this.sceneViewController.files;
  }

  get viewer() {
    return this.sceneViewController.viewer;
  }

  get picker() {
    return this.viewer.gpuPickers.markers;
  }

  updatePicker() {
    this.picker.needsUpdate = true;
  }

  getPickerResults(event: CustomMouseEvent): CameraImage {
    try {
      this.picker.update();

      const pickerID = this.picker.value(event);
      if (pickerID === null) return null;

      for (let file of this.files) {
        const camera = file.getByPickerID(pickerID);
        if (camera) return camera;
      }
    } catch (e) {
      console.log("Prevented framebuffer exception: ");
      console.log(e);
      return null;
    }
  }

  /** @private */
  markCameraSelected(camera: CameraImage) {
    for (let file of this.files) {
      file.setSelected(camera);
    }

    if (this.selected !== camera) {
      this.updatePicker();
    }

    this.selected = camera;
  }

  onMouseUp(event: CustomMouseEvent): CameraImage {
    return this.getPickerResults(event);
  }

  onMouseMove(event: CustomMouseEvent) {
    const camera = this.getPickerResults(event);
    this.markCameraSelected(camera);
  }

  setEnabled(state) {
    this.enabled = state;
  }

  setVisible(state) {
    if (!this.visible && state) {
      this.updatePicker();
    }

    this.visible = state;
  }
}

class ArrowNavigation {
  private sceneViewController: SceneViewCameras;
  private sections: ArrowMarker[] = [];
  private textures: Texture[] = [];
  public enabled = false;
  public visible = false;
  private object: Object3D;
  private needsUpdate = false;
  private minDistance = 5.0;
  private maxDistance = 40.0;
  private numSections = 8;
  private defaultScale = new Vector3(1.0, 1.0, 1.0);
  private zoomedScale = new Vector3(1.2, 1.2, 1.2);

  constructor(sceneViewCameras: SceneViewCameras) {
    this.sceneViewController = sceneViewCameras;

    this.initTextures();
    this.initObject();
    this.clearNavigation();
  }

  get files() {
    return this.sceneViewController.files;
  }

  get viewer() {
    return this.sceneViewController.viewer;
  }

  get scene() {
    return this.sceneViewController.scene;
  }

  initTextures() {
    this.textures.push(loader.load(arrowPathStandard)) ;
    this.textures.push(loader.load(arrowPathShadow));
  }

  initObject() {
    this.object = new Object3D();

    for (let section=0;section<this.numSections;section++) {
      let angle = this.sectionToAngle(section);
      let arrow = new ArrowMarker(this.textures, section, angle);

      this.object.add(arrow.object);
      this.sections.push(arrow);
    }

    this.scene.add(this.object);
  }

  onMouseMove(event) {
    this.sections.forEach(arrow => {
      arrow.setScale(this.defaultScale);
    });

    let object = this.getIntersect(event);
    if (!object) return;

    object.setScale(this.zoomedScale);
  }

  onMouseUp(event) {
    let object = this.getIntersect(event);
    if (!object || !object.camera) return null;

    this.sections.forEach(arrow => {
      arrow.setVisible(false);
    });

    return object.camera;
  }

  /** @private */
  getIntersect(event) : ArrowMarker {
    if (!this.visible) {
      return;
    }

    let targets = this.sections.map(arrow => {
      return arrow.object.children[0];
    });

    let raycaster = new RayCaster(this.viewer, event);
    let intersects = raycaster.intersectObjects(targets);
    if (intersects.length === 0) {
      return;
    }

    const intersect = intersects[0].object;
    const sectionID = intersect.userData.section;
    const object = this.sections[sectionID];

    return object;
  }

  setEnabled(state) {
    this.enabled = state;
  }

  setVisible(state) {
    this.visible = state;
  }

  angleToSection(angle) {
    let angleMod = (angle + 360) % 360;
    let section = angleMod / (360 / this.numSections);
    return Math.round(section % (this.numSections - 0.5));
  }

  sectionToAngle(section) {
    return section * (360 / this.numSections);
  }

  updateNavigation() {
    this.needsUpdate = true;
  }

  clearNavigation() {
    this.sections.forEach(arrow => {
      arrow.clear();
    });
  }

  calculateCameras() {
    if (!this.visible || !this.needsUpdate) return;

    this.needsUpdate = false;
    this.clearNavigation();

    const cameras = [];
    const cameraPos = this.viewer.camera.position;

    for (let file of this.files) {
      if (!file.visible) continue;

      file.cameras.forEach(camera => {
        const position = camera.position.scene;

        const dX = position.x - cameraPos.x;
        if (dX > this.maxDistance) return;

        const dY = position.y - cameraPos.y;
        if (dY > this.maxDistance) return;

        const distance = Math.sqrt(dX*dX + dY*dY);

        // Calculate min and max distance from camera;
        let tooClose = distance < this.minDistance;
        let tooFar = distance > this.maxDistance;
        if (tooClose || tooFar) return;

        // Calculate section
        let angle = Math.atan2(dY, dX) * 180 / Math.PI;
        let section = this.angleToSection(angle);

        cameras.push({
          camera,
          distance,
          section
        });
      });
    }

    cameras.sort((cameraA, cameraB) => {
      return cameraA.distance - cameraB.distance;
    });

    cameras.forEach(({camera, section}) => {
      if (this.sections[section].enabled) return;
      this.sections[section].setCamera(camera);
      this.sections[section].setVisible(true);
    });

    // Update mesh position
    this.object.position.copy(cameraPos);
  }

  update() {
    if (!this.object) return;
    this.calculateCameras();
    this.object.visible = this.visible;
  }
}

class ArrowMarker {
  public camera: CameraImage;
  private radius = 1.5
  private height = -1.0;
  private size = 0.5;
  public object: Object3D;

  constructor(textures, section, angle) {
    this.object = new Object3D();
    this.object.rotation.z = 1 * MathUtils.degToRad(angle);
    this.addMesh(section, textures[0], this.height);
    this.addMesh(section, textures[1], this.height * 1.03);
  }

  get visible() {
    return this.object.visible;
  }

  get enabled() {
    return this.camera !== null;
  }

  clear() {
    this.camera = null;
    this.setVisible(false);
  }

  setVisible(state) {
    this.object.visible = state;
  }

  setCamera(camera: CameraImage) {
    this.camera = camera;
  }

  setScale(scale) {
    this.object.children.forEach(x => {
      x.scale.copy(scale);
    });
  }

  addMesh(section, texture, height) {
    // Add standard marker mesh
    const geometryStandard = new PlaneGeometry(this.size * 0.75, this.size);
    const materialStandard = new MeshBasicMaterial({
      map: texture,
      side: DoubleSide,
      transparent: true,
      alphaTest: 0.5
    });

    const mesh = new Mesh(geometryStandard, materialStandard);
    mesh.userData = {section};
    mesh.position.x = this.radius;
    mesh.position.z = height;
    mesh.rotation.z = -1 * MathUtils.degToRad(90);
    this.object.add(mesh);
  }
}
